import { Card, Upload, Button, message, Space, Popconfirm, Alert, Select, Table, Tag } from 'antd'
import { UploadOutlined, FilePdfOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'

// نبني اسم القالب المخزَّن من نمط الفاتورة، بحيث لكل نمط قالبه
// المستقل تماماً (مبيعات ≠ مرتجعات مثلاً)، بدل قالب واحد يخدم الكل
const templateTypeFor = (billTypeGuid) => `bill_${billTypeGuid}`

function PdfTemplateUpload() {
  const [billTypes, setBillTypes] = useState([])
  const [templatesMap, setTemplatesMap] = useState({}) // template_type -> {file_name, updated_at}
  const [selectedType, setSelectedType] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    fetchAll()
  }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [typesRes, templatesRes] = await Promise.all([
        axios.get('/api/settings/bill-types', { headers }),
        axios.get('/api/settings/pdf-templates', { headers })
      ])
      setBillTypes(typesRes.data)
      setTemplatesMap(templatesRes.data)
    } catch (err) {
      message.error('خطأ في جلب البيانات')
    }
    setLoading(false)
  }

  const handleUpload = async ({ file }) => {
    if (!selectedType) {
      message.warning('اختر نمط الفاتورة أولاً')
      return
    }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('template_type', templateTypeFor(selectedType))

      await axios.post('/api/settings/pdf-template', formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      })
      message.success('تم رفع القالب بنجاح ✅')
      fetchAll()
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في رفع الملف')
    }
    setUploading(false)
  }

  const handleDelete = async (billTypeGuid) => {
    try {
      await axios.delete(`/api/settings/pdf-template/${templateTypeFor(billTypeGuid)}`, { headers })
      message.success('تم حذف القالب')
      fetchAll()
    } catch (err) {
      message.error('خطأ في الحذف')
    }
  }

  if (loading) return <div>جاري التحميل...</div>

  const selectedTemplate = selectedType ? templatesMap[templateTypeFor(selectedType)] : null

  const columns = [
    { title: 'نمط الفاتورة', dataIndex: 'name', key: 'name' },
    {
      title: 'حالة القالب',
      key: 'status',
      render: (_, record) => {
        const tpl = templatesMap[templateTypeFor(record.guid)]
        return tpl
          ? <Tag color="green" icon={<CheckCircleOutlined />}>{tpl.file_name}</Tag>
          : <Tag color="default">بدون قالب — يُستخدم التصميم الافتراضي</Tag>
      }
    },
    {
      title: 'آخر تحديث',
      key: 'updated_at',
      render: (_, record) => {
        const tpl = templatesMap[templateTypeFor(record.guid)]
        return tpl ? dayjs(tpl.updated_at).format('YYYY-MM-DD HH:mm') : '-'
      }
    }
  ]

  return (
    <Card title={<span><FilePdfOutlined /> قوالب تصميم الفاتورة (PDF)</span>} style={{ maxWidth: 720 }}>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
        message="لكل نمط فاتورة قالبه الخاص"
        description="ملفات التصميم يُعدّها فنّي متخصص عبر برنامج Stimulsoft. يمكنك رفع قالب مختلف لكل نمط (مبيعات، مرتجعات، ...) — الأنماط التي لا تملك قالباً مخصَّصاً تستخدم التصميم الافتراضي تلقائياً."
      />

      <Table
        columns={columns}
        dataSource={billTypes}
        rowKey="guid"
        pagination={false}
        size="small"
        style={{ marginBottom: 24 }}
      />

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
          اختر نمط الفاتورة لإدارة قالبه:
        </label>
        <Select
          style={{ width: '100%' }}
          size="large"
          placeholder="اختر نمط الفاتورة"
          value={selectedType}
          onChange={setSelectedType}
          options={billTypes.map(t => ({ label: t.name, value: t.guid }))}
        />
      </div>

      {selectedType && (
        <>
          {selectedTemplate ? (
            <div style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16
            }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ color: '#389e0d' }}>
                  <CheckCircleOutlined /> يوجد قالب مرفوع لهذا النمط
                </span>
                <span><strong>اسم الملف:</strong> {selectedTemplate.file_name}</span>
                <span><strong>آخر تحديث:</strong> {dayjs(selectedTemplate.updated_at).format('YYYY-MM-DD HH:mm')}</span>
              </Space>
            </div>
          ) : (
            <Alert
              type="warning"
              showIcon
              message="لا يوجد قالب لهذا النمط بعد — يُستخدم التصميم الافتراضي حالياً"
              style={{ marginBottom: 16 }}
            />
          )}

          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload
              accept=".mrt"
              showUploadList={false}
              customRequest={handleUpload}
              disabled={uploading}
            >
              <Button icon={<UploadOutlined />} loading={uploading} block size="large">
                {selectedTemplate ? 'استبدال القالب الحالي' : 'رفع قالب جديد (.mrt)'}
              </Button>
            </Upload>

            {selectedTemplate && (
              <Popconfirm
                title="هل تريد حذف القالب والرجوع للتصميم الافتراضي؟"
                okText="نعم" cancelText="لا"
                onConfirm={() => handleDelete(selectedType)}
              >
                <Button danger icon={<DeleteOutlined />} block>
                  حذف قالب هذا النمط
                </Button>
              </Popconfirm>
            )}
          </Space>
        </>
      )}

    </Card>
  )
}

export default PdfTemplateUpload
